/**
 * Level 4, Lessons 8–18: adding and subtracting fractions with the same bottom ("add the tops, keep the bottom —
 * nobody re-cut the strip"), equivalent fractions as a fair trade, a fraction times a whole number ("copies of a
 * fraction"), lines and angles (the corner test), triangles and quadrilaterals, symmetry (the fold test), perimeter
 * and area in cm, the metric ladder (King Henry…), and customary conversions.
 */
import { int, pick } from "../core";
import type { Item, Skill } from "../types";
import { fr, partsBar, SHAPES } from "../visuals";

/* ---------- L4.9 Add / subtract, same bottom ---------- */
export function fracAddSubItem(a: number, b: number, d: number, op: "+" | "−"): Item {
  const top = op === "+" ? a + b : a - b;
  return {
    id: `frac-addsub-same-bottom:${a}${op}${b}/${d}`,
    skill: "frac-addsub-same-bottom",
    prompt: `${fr(a, d)} ${op} ${fr(b, d)} = ?`,
    speak: `${a} over ${d} ${op === "+" ? "plus" : "minus"} ${b} over ${d}.`,
    visual: op === "+" ? `<div class="sbs"><figure>${partsBar(d, a, d > 8 ? 24 : 32)}</figure><figure>${partsBar(d, b, d > 8 ? 24 : 32)}</figure></div>` : partsBar(d, a, d > 8 ? 24 : 32),
    input: { kind: "fraction" },
    answer: `${top}/${d}`,
    diagnose: (raw) => {
      if (op === "+" && raw === `${top}/${2 * d}`) return { code: "frac-added-bottoms", hint: "Did anybody re-cut the strip? The pieces stay the same size — keep the bottom." };
      if (op === "−" && raw === `${top}/0`) return { code: "frac-added-bottoms", hint: "The pieces didn't disappear — they're still eighths. Keep the bottom." };
      return null;
    },
    hints: [`Same bottom: the pieces are all ${d === 2 ? "halves" : `the same size`}. It's just counting.`, `${op === "+" ? "Add" : "Subtract"} the tops, keep the bottom.`, `${a} ${op} ${b} = ${top}, still out of ${d}: ${top}/${d}.`],
  };
}
export const fracAddSub: Skill = {
  id: "frac-addsub-same-bottom",
  name: "Add and subtract fractions — same bottom",
  gen: (rng) => {
    const d = pick(rng, [3, 4, 5, 6, 8, 10, 12]); // 4.NF denominators only
    if (rng() < 0.5) { const a = int(rng, 1, d - 2), b = int(rng, 1, d - 1 - a); return fracAddSubItem(a, b, d, "+"); }
    const a = int(rng, 2, d - 1), b = int(rng, 1, a - 1); return fracAddSubItem(a, b, d, "−");
  },
};

/* ---------- L4.11 Equivalent: a fair trade? ---------- */
export function fairTradeItem(a: number, b: number, c: number, d: number): Item {
  const yes = a * d === b * c;
  return {
    id: `frac-equiv-yes-no:${a}/${b}=${c}/${d}`,
    skill: "frac-equiv-yes-no",
    prompt: `Are ${fr(a, b)} and ${fr(c, d)} equivalent — a fair trade?`,
    speak: `Are ${a} over ${b} and ${c} over ${d} equivalent?`,
    input: { kind: "choice", options: ["yes", "no"] },
    answer: yes ? "yes" : "no",
    hints: [`What was the bottom multiplied by? ${b} → ${d}.`, `${d / b} × ${b} = ${d}. Multiply the top by the same: ${a} × ${d / b} = ${a * (d / b)}.`, yes ? `${a * (d / b)} = ${c}: the same amount, renamed. Yes.` : `${a * (d / b)} isn't ${c}: not a fair trade. No.`],
  };
}
export const fairTrade: Skill = {
  id: "frac-equiv-yes-no",
  name: "Equivalent — a fair trade?",
  gen: (rng) => { const [b, k] = pick<[number, number]>(rng, [[2, 2], [2, 3], [2, 4], [2, 5], [3, 2], [3, 4], [4, 2], [4, 3], [5, 2], [6, 2]]), a = int(rng, 1, b - 1); const c = rng() < 0.5 ? a * k : a * k + pick(rng, [1, -1]); return fairTradeItem(a, b, Math.max(1, c), b * k); },
};

/* ---------- L4.12 A fraction times a whole number ---------- */
export function fracTimesWholeItem(n: number, a: number, d: number, repeated: boolean): Item {
  const top = n * a;
  const copies = Array(n).fill(`${a}/${d}`).join(" + ");
  return {
    id: `frac-times-whole:${n}x${a}/${d}:${repeated ? "r" : "s"}`,
    skill: "frac-times-whole",
    prompt: repeated ? `${n} × ${fr(a, d)} — ${n} copies of ${fr(a, d)}: ${Array(n).fill(fr(a, d)).join(" + ")} = ?` : `${n} × ${fr(a, d)} = ?`,
    speak: `${n} times ${a} over ${d}.`,
    input: { kind: "fraction" },
    answer: `${top}/${d}`,
    diagnose: (raw) => {
      if (n * d !== d && raw === `${top}/${n * d}`) return { code: "frac-times-both", hint: "Copies of the same piece — did the pieces change size? Keep the bottom." };
      if (a + n !== top && raw === `${a + n}/${d}`) return { code: "frac-added-whole", hint: `${n} copies, not ${n} more. ${n} × the top.` };
      return null;
    },
    hints: [`${n} copies of ${a}/${d}: ${copies}.`, "Multiply the whole number by the top. Keep the bottom.", `${n} × ${a} = ${top}: ${top}/${d}${top > d ? ` — more pieces than one whole holds` : ""}.`],
  };
}
export const fracTimesWhole: Skill = {
  id: "frac-times-whole",
  name: "A fraction times a whole number",
  gen: (rng, level) => { const d = pick(rng, [3, 4, 5, 6, 8, 10, 12]), a = level < 1 ? 1 : int(rng, 1, Math.min(3, d - 1)); return fracTimesWholeItem(int(rng, 2, level < 1 ? 5 : 7), a, d, level < 1); },
};

/* ---------- L4.13 Lines & angles: the corner test ---------- */
export function angleSvg(deg: number, size = 130): string {
  const cx = 18, cy = size - 18, L = size - 34, r = (deg * Math.PI) / 180;
  const x2 = cx + L * Math.cos(r), y2 = cy - L * Math.sin(r);
  const mark = Math.abs(deg - 90) < 0.5 ? `<path d="M${cx + 16},${cy} V${cy - 16} H${cx}" fill="none" stroke="var(--orange)" stroke-width="2"/>` : `<path d="M${cx + 22},${cy} A22,22 0 0 0 ${cx + 22 * Math.cos(r)},${cy - 22 * Math.sin(r)}" fill="none" stroke="var(--orange)" stroke-width="2"/>`;
  const W = deg > 90 ? size + 40 : size;
  const shift = deg > 90 ? 50 : 0;
  return `<svg width="${W}" height="${size}" role="img" aria-label="An angle"><g transform="translate(${shift},0)"><line x1="${cx}" y1="${cy}" x2="${cx + L}" y2="${cy}" stroke="var(--navy)" stroke-width="3"/><line x1="${cx}" y1="${cy}" x2="${x2}" y2="${y2}" stroke="var(--navy)" stroke-width="3"/>${mark}</g></svg>`;
}
const angleName = (deg: number) => (deg < 90 ? "acute" : deg === 90 ? "right" : "obtuse");
export function angleNameItem(deg: number): Item {
  const ans = angleName(deg);
  return {
    id: `angle-name:${deg}`, skill: "angle-name",
    prompt: "Name the angle: acute, right, or obtuse. Use the corner test.",
    visual: angleSvg(deg), input: { kind: "choice", options: ["acute", "right", "obtuse"] }, answer: ans,
    hints: ["Hold your paper's square corner in the angle.", "Smaller than the corner? Exactly the corner? Wider?", ans === "acute" ? "Smaller than the square corner: acute — the small, cute one." : ans === "right" ? "Exactly the corner: right (90°)." : "Wider than the corner: obtuse — the big, blunt one."],
  };
}
export const angleNameSkill: Skill = { id: "angle-name", name: "Acute, right or obtuse — the corner test", gen: (rng) => angleNameItem(pick(rng, [25, 35, 45, 55, 60, 90, 90, 115, 125, 135, 150])) };

export function linesSvg(kind: "parallel" | "perpendicular", tilt: number): string {
  const rot = `rotate(${tilt} 70 60)`;
  const inner = kind === "parallel" ? `<line x1="10" y1="40" x2="130" y2="40"/><line x1="10" y1="80" x2="130" y2="80"/>` : `<line x1="10" y1="60" x2="130" y2="60"/><line x1="70" y1="5" x2="70" y2="115"/>`;
  return `<svg width="140" height="120" role="img" aria-label="Two lines"><g transform="${rot}" stroke="var(--navy)" stroke-width="3">${inner}</g></svg>`;
}
export function linesItem(kind: "parallel" | "perpendicular", tilt: number): Item {
  return {
    id: `lines-parallel-perp:${kind}:${tilt}`, skill: "lines-parallel-perp",
    prompt: "Parallel or perpendicular?", visual: linesSvg(kind, tilt), input: { kind: "choice", options: ["parallel", "perpendicular"] }, answer: kind,
    hints: ["Do the lines ever meet?", "Parallel: side by side, never meet, like rail tracks. Perpendicular: cross at a square corner.", kind === "parallel" ? "They never meet: parallel." : "They cross at a square corner: perpendicular."],
  };
}
export const linesSkill: Skill = { id: "lines-parallel-perp", name: "Parallel or perpendicular", gen: (rng) => linesItem(rng() < 0.5 ? "parallel" : "perpendicular", pick(rng, [0, 20, -30, 45, 70])) };

export function angleAddItem(a: number, b: number): Item {
  const s = a + b;
  return {
    id: `angle-add:${a}+${b}`, skill: "angle-add",
    prompt: `Two angles sit side by side: <b>${a}° + ${b}°</b>. How big is the whole angle — and is it acute, right, or obtuse?`,
    input: { kind: "fields", fields: [{ width: 3, suffix: "°" }, { label: "it is", width: 4, text: true }] },
    answer: `${s}|${angleName(s)}`,
    hints: ["Angles side by side add.", `${a} + ${b}. Then compare with the square corner, 90°.`, `${s}° — ${angleName(s)}.`],
  };
}
export const angleAdd: Skill = { id: "angle-add", name: "Angles side by side add", gen: (rng) => { const a = pick(rng, [30, 40, 45, 50, 60, 90]); return angleAddItem(a, pick(rng, [20, 25, 30, 40, 45, 50, 60].filter((x) => a + x <= 150))); } };

/* ---------- L4.14 Triangles & quadrilaterals ---------- */
const tri = (pts: [number, number][]) => `<svg width="150" height="110" viewBox="0 0 150 110" role="img" aria-label="A triangle"><polygon points="${pts.map((p) => p.join(",")).join(" ")}" fill="none" stroke="var(--navy)" stroke-width="3" stroke-linejoin="round"/></svg>`;
export const TRIANGLES = {
  right: tri([[20, 95], [130, 95], [20, 15]]),
  acute: tri([[15, 95], [135, 95], [80, 12]]),
  obtuse: tri([[10, 95], [140, 95], [40, 45]]),
};
export function triangleItem(kind: "right" | "acute" | "obtuse"): Item {
  return {
    id: `triangle-name:${kind}`, skill: "triangle-name",
    prompt: "Name this triangle by its biggest angle: right, acute, or obtuse.", visual: TRIANGLES[kind],
    input: { kind: "choice", options: ["right", "acute", "obtuse"] }, answer: kind,
    hints: ["You only have to check the biggest corner.", "Test it with your square corner.", kind === "right" ? "One square corner: right." : kind === "acute" ? "Every corner smaller than square: acute." : "One corner wider than square: obtuse."],
  };
}
export const triangleSkill: Skill = { id: "triangle-name", name: "Name the triangle by its biggest angle", gen: (rng) => triangleItem(pick(rng, ["right", "acute", "obtuse"] as const)) };

const QUADS = ["square", "rectangle", "rhombus", "parallelogram", "trapezoid"] as const;
const QUAD_RULE: Record<(typeof QUADS)[number], string> = {
  square: "4 equal sides and 4 square corners",
  rectangle: "4 square corners, opposite sides equal",
  rhombus: "4 equal sides, corners not square",
  parallelogram: "opposite sides parallel and equal, corners not square",
  trapezoid: "exactly one pair of parallel sides",
};
export function quadItem(q: (typeof QUADS)[number], byWords: boolean): Item {
  return {
    id: `quad-name:${q}:${byWords ? "w" : "p"}`, skill: "quad-name",
    prompt: byWords ? `A quadrilateral with <b>${QUAD_RULE[q]}</b>. What is it?` : "Name this quadrilateral — the most exact name.",
    visual: byWords ? undefined : SHAPES[q],
    input: { kind: "choice", options: [...QUADS] }, answer: q,
    diagnose: (raw) => (q === "square" && (raw === "rectangle" || raw === "rhombus") ? { code: "quad-true-not-exact", hint: `True — a square passes that test too. Is there a more exact name?` } : null),
    hints: ["Check its parts: equal sides? Square corners? Parallel sides?", "A name is just a description of the parts.", `${QUAD_RULE[q]}: ${q}.`],
  };
}
export const quadSkill: Skill = { id: "quad-name", name: "Name the quadrilateral", gen: (rng) => quadItem(pick(rng, [...QUADS]), rng() < 0.4) };

/* ---------- L4.15 Symmetry: the fold test ---------- */
const iso = `<svg width="110" height="110" viewBox="0 0 110 110" role="img" aria-label="A triangle with two equal sides"><polygon points="55,8 95,100 15,100" fill="none" stroke="var(--navy)" stroke-width="3"/></svg>`;
const SYM: { name: string; svg: string; lines: number }[] = [
  { name: "square", svg: SHAPES.square, lines: 4 },
  { name: "rectangle (not a square)", svg: SHAPES.rectangle, lines: 2 },
  { name: "equilateral triangle", svg: SHAPES.triangle, lines: 3 },
  { name: "isosceles triangle", svg: iso, lines: 1 },
  { name: "scalene triangle", svg: SHAPES.scalene, lines: 0 },
];
export function symmetryItem(i: number): Item {
  const s = SYM[i]!;
  return {
    id: `symmetry-count:${s.name}`, skill: "symmetry-count",
    prompt: `How many lines of symmetry does this <b>${s.name}</b> have? Use the fold test.`, visual: s.svg,
    input: { kind: "number" }, answer: String(s.lines),
    diagnose: (raw) => (s.lines === 2 && raw.trim() === "4" ? { code: "symmetry-rectangle-trap", hint: "The rectangle trap: fold it corner to corner. Do the halves land exactly on top of each other?" } : null),
    hints: ["Fold it in your mind: do the two halves land exactly on top of each other?", "Try up-and-down, across, and corner to corner.", `${s.lines} line${s.lines === 1 ? "" : "s"} of symmetry.`],
  };
}
export const symmetrySkill: Skill = { id: "symmetry-count", name: "Count the lines of symmetry", gen: (rng) => symmetryItem(int(rng, 0, SYM.length - 1)) };

/* ---------- L4.16 Perimeter and area of one rectangle ---------- */
export function perimAreaItem(l: number, w: number): Item {
  return {
    id: `perim-area-both:${l}x${w}`, skill: "perim-area-both",
    prompt: `A garden is <b>${l} cm</b> long and <b>${w} cm</b> wide. How much fence goes all the way AROUND (perimeter)? How much grass covers the INSIDE (area)?`,
    input: { kind: "fields", fields: [{ label: "Perimeter", width: 3, suffix: "cm" }, { label: "Area", width: 3, suffix: "sq cm" }] },
    answer: `${2 * (l + w)}|${l * w}`,
    diagnose: (raw) => (raw === `${l * w}|${2 * (l + w)}` && l * w !== 2 * (l + w) ? { code: "area-perimeter", hint: "Switched: perimeter is the walk around the edge; area is the squares inside." } : null),
    hints: ["Perimeter: walk the fence line — add all four sides.", `${l} + ${w} + ${l} + ${w}. Area: ${w} rows of ${l} squares.`, `P = ${l} + ${w} + ${l} + ${w} = ${2 * (l + w)} cm; A = ${l} × ${w} = ${l * w} sq cm.`],
  };
}
export const perimArea: Skill = { id: "perim-area-both", name: "Perimeter and area of a rectangle (cm)", gen: (rng) => { let w = int(rng, 2, 8), l = int(rng, w, 12); if (l * w === 2 * (l + w)) l++; return perimAreaItem(l, w); } };

/* ---------- L4.17 The metric ladder ---------- */
export const RUNGS = ["K", "H", "D", "U", "d", "c", "m"];
const RUNG_WORDS = ["kilo (King)", "hecto (Henry)", "deka (Died)", "unit (Unexpectedly)", "deci (Drinking)", "centi (Chocolate)", "milli (Milk)"];
/** The graph-paper ladder: 7 lines, the decimal staircase one square right on each line down. Optionally a number on a line. */
export function ladderSvg(num?: string, line?: number, landed?: { num: string; line: number }): string {
  const u = 28, cols = 11, left = 185, W = left + cols * u + 6, H = 7 * u + 10;
  let s = `<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" role="img" aria-label="The metric ladder: King Henry Died Unexpectedly Drinking Chocolate Milk"><g>`;
  for (let r = 0; r < 7; r++) {
    const y = 5 + r * u;
    s += `<text x="${left - 8}" y="${y + 17}" text-anchor="end" font-size="13" fill="var(--ink)" font-weight="${r === 3 ? 700 : 400}">${RUNGS[r]} — ${RUNG_WORDS[r]}</text>`;
    for (let c = 0; c < cols; c++) s += `<rect x="${left + c * u}" y="${y}" width="${u}" height="${u}" fill="${r === 3 ? "var(--sky)" : "var(--bg)"}" stroke="var(--line)"/>`;
    const dx = left + (3 + r) * u;
    s += `<circle cx="${dx}" cy="${y + u - 3}" r="3" fill="var(--navy)"/>`;
  }
  const put = (n: string, r: number, color: string) => {
    const [i, f = ""] = n.split(".");
    const digits = i!.split(""), y = 5 + r * u + 17, end = 3 + r;
    digits.forEach((d, k) => (s += `<text x="${left + (end - digits.length + k) * u + u / 2}" y="${y}" text-anchor="middle" font-weight="700" font-size="16" fill="${color}">${d}</text>`));
    f.split("").forEach((d, k) => (s += `<text x="${left + (end + k) * u + u / 2}" y="${y}" text-anchor="middle" font-weight="700" font-size="16" fill="${color}">${d}</text>`));
  };
  if (num !== undefined && line !== undefined) put(num, line, "var(--orange)");
  if (landed) put(landed.num, landed.line, "var(--navy)");
  return s + `</g></svg>`;
}
const UNIT_NAMES: Record<string, [string, number]> = { km: ["kilometers", 0], m: ["meters", 3], cm: ["centimeters", 5], mm: ["millimeters", 6], L: ["liters", 3], mL: ["milliliters", 6], kg: ["kilograms", 0], g: ["grams", 3] };
/** Convert by moving digits straight up or down the ladder. */
export function metricItem(n: string, from: string, to: string): Item {
  const [fname, fr0] = UNIT_NAMES[from]!, [tname, tr] = UNIT_NAMES[to]!;
  const steps = tr - fr0; // down = positive = bigger number
  const val = Math.round(Number(n) * 10 ** steps * 1e6) / 1e6;
  const ans = String(val);
  return {
    id: `metric-ladder:${n}${from}->${to}`, skill: "metric-ladder",
    prompt: `Use the ladder: <b>${n} ${from} = ? ${to}</b>`,
    speak: `${n} ${fname} equals how many ${tname}?`,
    visual: ladderSvg(n, fr0),
    input: { kind: "number", label: to },
    answer: ans,
    alsoAccept: val < 1 ? [ans.replace(/^0/, ""), (Math.round(val * 100) / 100).toFixed(2)] : [],
    diagnose: (raw) => {
      const r = Number(raw.trim());
      if (r === Number(n) * 10 ** -steps && steps !== 0) return { code: "metric-wrong-way", hint: `${from} to ${to}: is that up the ladder or down? Going down, the number gets bigger.` };
      if (raw.includes(".") && raw.split(".").length > 2) return { code: "metric-moved-decimal", hint: "One decimal only — the one printed on the line you land on. Move only the digits." };
      if (r !== val && (r === val * 10 || r === val / 10)) return { code: "metric-zeros", hint: `Count the lines from ${from} to ${to}: ${Math.abs(steps)}. Fill every empty square with a zero.` };
      return null;
    },
    hints: [`Write ${n} on the ${RUNGS[fr0]} line. Which line is ${to}?`, `Move the digits straight ${steps > 0 ? "down" : "up"} their columns to the ${RUNGS[tr]} line — never sideways. Fill empty squares with zeros.`, `Read the decimal printed on the ${RUNGS[tr]} line: ${n} ${from} = ${ans} ${to}.`],
  };
}
export const metricSkill: Skill = {
  id: "metric-ladder", name: "Metric conversions — the ladder",
  gen: (rng, level) => {
    // Level 4: whole numbers, large unit to small (4.MD.1). Decimals on the ladder are Level 5.
    const k = pick(rng, level < 1 ? [0, 1, 2, 5] : [0, 1, 2, 3, 4, 5]);
    if (k === 0) return metricItem(String(int(rng, 2, 9)), "km", "m");
    if (k === 1) return metricItem(String(int(rng, 2, 9)), "m", "cm");
    if (k === 2) return metricItem(String(int(rng, 2, 9)), "L", "mL");
    if (k === 3) return metricItem(String(int(rng, 12, 48)), "m", "cm");
    if (k === 4) return metricItem(String(int(rng, 11, 25)), "kg", "g");
    return metricItem(String(int(rng, 2, 9)), "kg", "g");
  },
};

/* ---------- L4.18 Customary: a bigger unit to a smaller unit — multiply by how many fit inside one ---------- */
export function customaryItem(n: number, kind: "ft" | "gal"): Item {
  const k = kind === "ft" ? 12 : 4, [big, small] = kind === "ft" ? ["ft", "in"] : ["gal", "qt"];
  return {
    id: `customary:${n}${kind}`, skill: "customary",
    prompt: `<b>${n} ${big} = ? ${small}</b>`,
    speak: `${n} ${kind === "ft" ? "feet" : "gallons"} equals how many ${kind === "ft" ? "inches" : "quarts"}?`,
    input: { kind: "number", label: small }, answer: String(n * k),
    diagnose: (raw) => { const r = Number(raw.trim()); if (r === n * 10 || r === n * 100) return { code: "customary-base10", hint: "Customary units don't climb by tens — the ladder stays home." }; if (r === n + k) return { code: "customary-added", hint: `${k} fit inside every one — multiply.` }; return null; },
    hints: [`A bigger unit to a smaller unit — multiply by how many fit inside one.`, kind === "ft" ? "1 ft = 12 in. Count by 12s." : "1 gal = 4 qt. Count by 4s.", `${n} × ${k} = ${n * k} ${small}.`],
  };
}
export const customarySkill: Skill = { id: "customary", name: "Feet to inches, gallons to quarts", gen: (rng) => (rng() < 0.5 ? customaryItem(int(rng, 2, 12), "ft") : customaryItem(int(rng, 2, 9), "gal")) };

export const LEVEL4_L8TO18_SKILLS: Skill[] = [fracAddSub, fairTrade, fracTimesWhole, angleNameSkill, linesSkill, angleAdd, triangleSkill, quadSkill, symmetrySkill, perimArea, metricSkill, customarySkill];
